// src/components/NewClientModal.jsx
import React, { useState, useEffect } from 'react';
import { X, User, Building2, MapPin, Plus, Trash2 } from 'lucide-react';

const NewClientModal = ({ onClose, onSave, initialName = '', isQuickCreate = false }) => {
    const [formData, setFormData] = useState({
        clientName: initialName,
        clientEmail: '',
        clientPhone: '',
        companyName: '',
        address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'Lebanon'
        },
        contactPersons: [
            {
                name: '',
                email: '',
                phone: '',
                position: '',
                isPrimary: true
            }
        ],
        industry: '',
        clientType: 'Company',
        status: 'active',
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const industries = [
        'Construction',
        'Real Estate',
        'Manufacturing',
        'Healthcare',
        'Education',
        'Government',
        'Hospitality',
        'Retail',
        'Other'
    ];

    const clientTypes = [
        'Individual',
        'Company',
        'Government',
        'NGO'
    ];

    const validateForm = () => {
        const newErrors = {};

        if (!formData.clientName.trim()) {
            newErrors.clientName = 'Client name is required';
        }

        if (!formData.clientEmail.trim()) {
            newErrors.clientEmail = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.clientEmail)) {
            newErrors.clientEmail = 'Please enter a valid email address';
        }

        // Validate contact persons
        formData.contactPersons.forEach((contact, index) => {
            if (contact.name && contact.email && !/\S+@\S+\.\S+/.test(contact.email)) {
                newErrors[`contactPersons.${index}.email`] = 'Please enter a valid email address';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleContactPersonChange = (index, field, value) => {
        const updatedContacts = [...formData.contactPersons];
        updatedContacts[index] = {
            ...updatedContacts[index],
            [field]: value
        };

        setFormData(prev => ({
            ...prev,
            contactPersons: updatedContacts
        }));

        // Clear error
        const errorKey = `contactPersons.${index}.${field}`;
        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: '' }));
        }
    };

    const addContactPerson = () => {
        setFormData(prev => ({
            ...prev,
            contactPersons: [
                ...prev.contactPersons,
                {
                    name: '',
                    email: '',
                    phone: '',
                    position: '',
                    isPrimary: false
                }
            ]
        }));
    };

    const removeContactPerson = (index) => {
        if (formData.contactPersons.length > 1) {
            const updatedContacts = formData.contactPersons.filter((_, i) => i !== index);
            
            // If we removed the primary contact, make the first one primary
            if (formData.contactPersons[index].isPrimary && updatedContacts.length > 0) {
                updatedContacts[0].isPrimary = true;
            }

            setFormData(prev => ({
                ...prev,
                contactPersons: updatedContacts
            }));
        }
    };

    const setPrimaryContact = (index) => {
        const updatedContacts = formData.contactPersons.map((contact, i) => ({
            ...contact,
            isPrimary: i === index
        }));

        setFormData(prev => ({
            ...prev,
            contactPersons: updatedContacts
        }));
    };

const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
        return;
    }

    setIsSubmitting(true);

    try {
        // SIMPLIFIED data - just send the minimum required fields first
        const clientData = {
            clientName: formData.clientName.trim(),
            clientEmail: formData.clientEmail.trim().toLowerCase()
        };

        console.log('🚀 Sending minimal client data:', clientData);
        await onSave(clientData);
    } catch (error) {
        console.error('❌ Error saving client:', error);
        alert('Failed to save client: ' + error.message);
    } finally {
        setIsSubmitting(false);
    }
};
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: isQuickCreate ? '600px' : '800px',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '700',
                            color: '#1f2937',
                            margin: '0 0 4px 0'
                        }}>
                            New Client
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            margin: 0
                        }}>
                            {isQuickCreate ? 
                                'Quick client creation for quotation' : 
                                'Add a new client to your database'
                            }
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#6b7280'
                        }}
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <div style={{
                    padding: '24px',
                    overflowY: 'auto',
                    maxHeight: 'calc(90vh - 140px)'
                }}>
                    <form onSubmit={handleSubmit}>
                        {/* Basic Information */}
                        <div style={{ marginBottom: '32px' }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#1f2937',
                                marginBottom: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <User size={20} />
                                Basic Information
                            </h3>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isQuickCreate ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '16px'
                            }}>
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Client Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.clientName}
                                        onChange={(e) => handleInputChange('clientName', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: `1px solid ${errors.clientName ? '#dc2626' : '#d1d5db'}`,
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                        placeholder="Enter client name"
                                    />
                                    {errors.clientName && (
                                        <p style={{
                                            fontSize: '12px',
                                            color: '#dc2626',
                                            marginTop: '4px',
                                            margin: '4px 0 0 0'
                                        }}>
                                            {errors.clientName}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.clientEmail}
                                        onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: `1px solid ${errors.clientEmail ? '#dc2626' : '#d1d5db'}`,
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                        placeholder="client@example.com"
                                    />
                                    {errors.clientEmail && (
                                        <p style={{
                                            fontSize: '12px',
                                            color: '#dc2626',
                                            marginTop: '4px',
                                            margin: '4px 0 0 0'
                                        }}>
                                            {errors.clientEmail}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.clientPhone}
                                        onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                        placeholder="+961 1 234567"
                                    />
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.companyName}
                                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            outline: 'none'
                                        }}
                                        placeholder="Company name (optional)"
                                    />
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Client Type
                                    </label>
                                    <select
                                        value={formData.clientType}
                                        onChange={(e) => handleInputChange('clientType', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    >
                                        {clientTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '6px'
                                    }}>
                                        Industry
                                    </label>
                                    <select
                                        value={formData.industry}
                                        onChange={(e) => handleInputChange('industry', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            outline: 'none'
                                        }}
                                    >
                                        <option value="">Select industry (optional)</option>
                                        {industries.map(industry => (
                                            <option key={industry} value={industry}>{industry}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Address Information - Only show in full mode */}
                        {!isQuickCreate && (
                            <div style={{ marginBottom: '32px' }}>
                                <h3 style={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#1f2937',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <MapPin size={20} />
                                    Address Information
                                </h3>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '16px'
                                }}>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address.street}
                                            onChange={(e) => handleInputChange('address.street', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="Street address"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address.city}
                                            onChange={(e) => handleInputChange('address.city', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="City"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            State/Province
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address.state}
                                            onChange={(e) => handleInputChange('address.state', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="State/Province"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            ZIP/Postal Code
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address.zipCode}
                                            onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="ZIP/Postal Code"
                                        />
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#374151',
                                            marginBottom: '6px'
                                        }}>
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.address.country}
                                            onChange={(e) => handleInputChange('address.country', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                outline: 'none'
                                            }}
                                            placeholder="Country"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Contact Persons - Only show in full mode */}
                        {!isQuickCreate && (
                            <div style={{ marginBottom: '32px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <h3 style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#1f2937',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <Building2 size={20} />
                                        Contact Persons
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={addContactPerson}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 12px',
                                            backgroundColor: '#1e40af',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Plus size={14} />
                                        Add Contact
                                    </button>
                                </div>

                                {formData.contactPersons.map((contact, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            padding: '16px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            marginBottom: '12px',
                                            backgroundColor: contact.isPrimary ? '#f0f9ff' : '#f9fafb'
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px'
                                            }}>
                                                <h4 style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#1f2937',
                                                    margin: 0
                                                }}>
                                                    Contact Person {index + 1}
                                                </h4>
                                                {contact.isPrimary && (
                                                    <span style={{
                                                        padding: '2px 8px',
                                                        backgroundColor: '#1e40af',
                                                        color: 'white',
                                                        borderRadius: '12px',
                                                        fontSize: '10px',
                                                        fontWeight: '600'
                                                    }}>
                                                        PRIMARY
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                {!contact.isPrimary && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPrimaryContact(index)}
                                                        style={{
                                                            padding: '4px 8px',
                                                            backgroundColor: 'white',
                                                            color: '#1e40af',
                                                            border: '1px solid #1e40af',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: '500',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        Set Primary
                                                    </button>
                                                )}
                                                {formData.contactPersons.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeContactPerson(index)}
                                                        style={{
                                                            padding: '4px',
                                                            backgroundColor: '#fef2f2',
                                                            color: '#dc2626',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                            gap: '12px'
                                        }}>
                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    marginBottom: '4px'
                                                }}>
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={contact.name}
                                                    onChange={(e) => handleContactPersonChange(index, 'name', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        outline: 'none'
                                                    }}
                                                    placeholder="Contact name"
                                                />
                                            </div>

                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    marginBottom: '4px'
                                                }}>
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={contact.email}
                                                    onChange={(e) => handleContactPersonChange(index, 'email', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: `1px solid ${errors[`contactPersons.${index}.email`] ? '#dc2626' : '#d1d5db'}`,
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        outline: 'none'
                                                    }}
                                                    placeholder="contact@example.com"
                                                />
                                                {errors[`contactPersons.${index}.email`] && (
                                                    <p style={{
                                                        fontSize: '10px',
                                                        color: '#dc2626',
                                                        marginTop: '2px',
                                                        margin: '2px 0 0 0'
                                                    }}>
                                                        {errors[`contactPersons.${index}.email`]}
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    marginBottom: '4px'
                                                }}>
                                                    Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={contact.phone}
                                                    onChange={(e) => handleContactPersonChange(index, 'phone', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        outline: 'none'
                                                    }}
                                                    placeholder="Phone number"
                                                />
                                            </div>

                                            <div>
                                                <label style={{
                                                    display: 'block',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    color: '#374151',
                                                    marginBottom: '4px'
                                                }}>
                                                    Position
                                                </label>
                                                <input
                                                    type="text"
                                                    value={contact.position}
                                                    onChange={(e) => handleContactPersonChange(index, 'position', e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        outline: 'none'
                                                    }}
                                                    placeholder="Job title"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Notes - Only show in full mode */}
                        {!isQuickCreate && (
                            <div style={{ marginBottom: '32px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '6px'
                                }}>
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        resize: 'vertical',
                                        minHeight: '80px'
                                    }}
                                    placeholder="Additional notes about the client..."
                                    rows="3"
                                />
                            </div>
                        )}
                        {/* Form Actions */}
                       <div style={{
                           display: 'flex',
                           gap: '12px',
                           justifyContent: 'flex-end',
                           paddingTop: '20px',
                           borderTop: '1px solid #e5e7eb'
                       }}>
                           <button
                               type="button"
                               onClick={onClose}
                               style={{
                                   padding: '12px 20px',
                                   backgroundColor: 'white',
                                   color: '#374151',
                                   border: '1px solid #d1d5db',
                                   borderRadius: '8px',
                                   fontSize: '14px',
                                   fontWeight: '600',
                                   cursor: 'pointer'
                               }}
                           >
                               Cancel
                           </button>

                           <button
                               type="submit"
                               disabled={isSubmitting}
                               style={{
                                   padding: '12px 20px',
                                   backgroundColor: isSubmitting ? '#9ca3af' : '#1e40af',
                                   color: 'white',
                                   border: 'none',
                                   borderRadius: '8px',
                                   fontSize: '14px',
                                   fontWeight: '600',
                                   cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                   transition: 'all 0.2s ease'
                               }}
                           >
                               {isSubmitting ? 'Creating...' : 'Create Client'}
                           </button>
                       </div>
                   </form>
               </div>
           </div>
       </div>
   );
};

export default NewClientModal;